import { useCallback } from 'react';

import {
  SEARCHABLE_VENDOR_TAXONOMY_KEYS,
  SearchableVendorTaxonomyKey,
  VENUES_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorWordCategory } from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import {
  InlineAccordion,
  InlineAccordionItemType,
} from '@zola/zola-ui/src/components/InlineAccordion/InlineAccordion';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import { Title2 } from '@zola/zola-ui/src/typography/Headings';

import cx from 'classnames';

import { US_STATES } from '~/components/common/form/addresses';
import createCanonicalUrl from '~/pages/couples/explore/util/canonicalUrl';
import { LocationOrAllMarketsType } from '~/types/responseTypes';
import { OptionFacetType } from '~/types/types';
import { NYC_BOROUGHS, isBorough } from '~/util/locationUtils';

import { PageSection } from '../../../PageSection/PageSection';
import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { PartialSearchLocationType, PartialVendorSearch } from '../../types/types';
import { getCanonicalFacetDescription } from '../../utils/canonicalFacetHelpers';

import styles from './seoCrossLinks.module.less';

const getContentItem = (text: string, url: string): ContentItem => ({ text, url });

const Title = ({ text }: { text: string }) => (
  <Title2 className={styles.title} presentation="h6">
    {text}
  </Title2>
);

type ContentItem = {
  text: string;
  url: string;
};

const Content = ({ items }: { items: ContentItem[] }) => (
  <ul className={styles.list}>
    {items.map((item, index) => (
      <li key={index}>
        <LinkV2 className={styles.link} href={item.url} noTextTransform subtle>
          {item.text}
        </LinkV2>
      </li>
    ))}
  </ul>
);

interface SeoCrossLinksProps {
  className?: string;
}

export const SeoCrossLinks = ({ className }: SeoCrossLinksProps) => {
  const { vendorSearch, facetsByVendorType, seoTopLocations, seoNearbyCities } =
    useSearchResultsPageContext();
  const { selectedFacets, selectedLocation, selectedTaxonomy } = vendorSearch;
  const hasSelectedFacets = selectedFacets && selectedFacets.length > 0;
  const venueTypeFacet = selectedFacets?.find(
    (facet) => facet.parentKey === 'target-couple-venue-preference'
  );
  const items: InlineAccordionItemType[] = [];

  const getSearchUrl = useCallback(
    ({
      location,
      taxonomyKey,
      facets,
    }: {
      location?: LocationOrAllMarketsType | PartialSearchLocationType;
      taxonomyKey?: SearchableVendorTaxonomyKey;
      facets?: OptionFacetType[];
    }) => {
      const newVendorSearch: PartialVendorSearch = { ...vendorSearch };
      if (location) {
        newVendorSearch.selectedLocation = location;
      }
      if (taxonomyKey) {
        newVendorSearch.selectedTaxonomy = { key: taxonomyKey };
      }
      if (facets) {
        newVendorSearch.selectedFacets = facets;
      }
      return createCanonicalUrl(newVendorSearch, facetsByVendorType);
    },
    [facetsByVendorType, vendorSearch]
  );

  // Links for no location + venue + venue type SRPs
  if (
    selectedLocation?.type === 'ALL_MARKETS' &&
    selectedTaxonomy?.key === VENUES_TAXONOMY_KEY &&
    venueTypeFacet
  ) {
    const venueType = getCanonicalFacetDescription(VENUES_TAXONOMY_KEY, [venueTypeFacet], true);

    // Venue type in popular cities
    items.push({
      title: <Title text={`${venueType} in Popular Cities`} />,
      content: (
        <Content
          items={seoTopLocations.map((location) =>
            getContentItem(
              `${venueType} Wedding Venues in ${location.label}`,
              getSearchUrl({ location, facets: [venueTypeFacet] })
            )
          )}
        />
      ),
    });

    // Venue type by state
    items.push({
      title: <Title text={`${venueType} by State`} />,
      content: (
        <Content
          items={US_STATES.map((state) =>
            getContentItem(
              `${venueType} Wedding Venues in ${state.name}`,
              getSearchUrl({ location: state, facets: [venueTypeFacet] })
            )
          )}
        />
      ),
    });
  }

  // Links for state + venue + venue type SRPs
  if (
    selectedLocation?.type === 'STATE' &&
    selectedTaxonomy?.key === VENUES_TAXONOMY_KEY &&
    venueTypeFacet
  ) {
    const venueType = getCanonicalFacetDescription(VENUES_TAXONOMY_KEY, [venueTypeFacet], true);
    const venueFacets = facetsByVendorType[VENUES_TAXONOMY_KEY] || [];
    const venueTypeFacets = Object.values(venueFacets).filter(
      (facet) => facet.parentKey === 'target-couple-venue-preference'
    );

    // Other venue types in state
    if (venueTypeFacets) {
      items.push({
        title: <Title text={`Other Venue Types in ${selectedLocation.label}`} />,
        content: (
          <Content
            items={venueTypeFacets.map((facet) => {
              const facetDescription = getCanonicalFacetDescription(
                VENUES_TAXONOMY_KEY,
                [facet],
                true
              );
              return getContentItem(
                `${facetDescription} Wedding Venues in ${selectedLocation.label}`,
                getSearchUrl({ facets: [facet] })
              );
            })}
          />
        ),
      });
    }

    // Popular cities in state
    items.push({
      title: <Title text={`Popular Cities in ${selectedLocation.label}`} />,
      content: (
        <Content
          items={seoTopLocations.map((location) =>
            getContentItem(
              `${venueType} Wedding Venues in ${location.label}`,
              getSearchUrl({ location })
            )
          )}
        />
      ),
    });

    // Other vendors in state
    items.push({
      title: <Title text={`Other ${selectedLocation.label} Vendors`} />,
      content: (
        <Content
          items={SEARCHABLE_VENDOR_TAXONOMY_KEYS.map((taxonomyKey) => {
            const searchableCategory = getVendorWordCategory(taxonomyKey);
            return getContentItem(
              `Wedding ${searchableCategory} in ${selectedLocation.label}`,
              getSearchUrl({ taxonomyKey, facets: [] })
            );
          })}
        />
      ),
    });
  }

  // Links for city + venue + venue type SRPs
  if (
    selectedLocation?.type === 'CITY' &&
    selectedTaxonomy?.key === VENUES_TAXONOMY_KEY &&
    venueTypeFacet
  ) {
    const venueFacets = facetsByVendorType[VENUES_TAXONOMY_KEY] || [];
    const venueTypeFacets = Object.values(venueFacets).filter(
      (facet) => facet.parentKey === 'target-couple-venue-preference'
    );

    // Other venue types in city
    if (venueTypeFacets) {
      items.push({
        title: <Title text={`Other Venue Types in ${selectedLocation.label}`} />,
        content: (
          <Content
            items={venueTypeFacets.map((facet) => {
              const facetDescription = getCanonicalFacetDescription(
                VENUES_TAXONOMY_KEY,
                [facet],
                true
              );
              return getContentItem(
                `${facetDescription} Wedding Venues in ${selectedLocation.label}`,
                getSearchUrl({ facets: [facet] })
              );
            })}
          />
        ),
      });
    }

    // Other vendors in city
    items.push({
      title: <Title text={`Other Vendors in ${selectedLocation.label}`} />,
      content: (
        <Content
          items={SEARCHABLE_VENDOR_TAXONOMY_KEYS.map((taxonomyKey) => {
            const searchableCategory = getVendorWordCategory(taxonomyKey);
            return getContentItem(
              `Wedding ${searchableCategory} in ${selectedLocation.label}`,
              getSearchUrl({ taxonomyKey, facets: [] })
            );
          })}
        />
      ),
    });

    // Nearby venues
    if (seoNearbyCities.length > 0) {
      items.push({
        title: <Title text="Nearby Venues" />,
        content: (
          <Content
            items={seoNearbyCities.map((city) =>
              getContentItem(
                `Wedding Venues in ${city.name}, ${city.stateCode}`,
                getSearchUrl({ location: city })
              )
            )}
          />
        ),
      });

      // Nearby vendors
      items.push({
        title: <Title text="Nearby Vendors" />,
        content: (
          <Content
            items={seoNearbyCities.map((city) =>
              getContentItem(
                `Wedding Vendors in ${city.name}, ${city.stateCode}`,
                getSearchUrl({ location: city })
              )
            )}
          />
        ),
      });
    }
  }

  // Links for state + category SRPs
  if (selectedLocation?.type === 'STATE' && selectedTaxonomy && !hasSelectedFacets) {
    const category = getVendorWordCategory(selectedTaxonomy.key);

    // Category in top cities in state
    items.push({
      title: <Title text={`Wedding ${category} in Top Cities in ${selectedLocation.label}`} />,
      content: (
        <Content
          items={seoTopLocations.map((location) =>
            getContentItem(`Wedding ${category} in ${location.label}`, getSearchUrl({ location }))
          )}
        />
      ),
    });

    // Wedding vendors in state
    items.push({
      title: <Title text={`Wedding Vendors in ${selectedLocation.label}`} />,
      content: (
        <Content
          items={SEARCHABLE_VENDOR_TAXONOMY_KEYS.map((taxonomyKey) => {
            const searchableCategory = getVendorWordCategory(taxonomyKey);
            return getContentItem(
              `Wedding ${searchableCategory} in ${selectedLocation.label}`,
              getSearchUrl({ taxonomyKey })
            );
          })}
        />
      ),
    });
  }

  // Links for state + no category SRPs
  if (selectedLocation?.type === 'STATE' && !selectedTaxonomy && !hasSelectedFacets) {
    // Wedding vendors in top cities in state
    items.push({
      title: <Title text={`Wedding Vendors in ${selectedLocation.label}`} />,
      content: (
        <Content
          items={seoTopLocations.map((location) =>
            getContentItem(`Wedding Vendors in ${location.label}`, getSearchUrl({ location }))
          )}
        />
      ),
    });
  }

  // Links for city + no category SRPs
  if (
    selectedLocation?.type === 'CITY' &&
    selectedLocation.slug !== 'new-york-ny' &&
    !isBorough(selectedLocation) &&
    !selectedTaxonomy &&
    !hasSelectedFacets
  ) {
    // Wedding vendors in city
    items.push({
      title: <Title text={`Wedding Vendors in ${selectedLocation.label}`} />,
      content: (
        <Content
          items={SEARCHABLE_VENDOR_TAXONOMY_KEYS.map((taxonomyKey) => {
            const searchableCategory = getVendorWordCategory(taxonomyKey);
            return getContentItem(
              `Wedding ${searchableCategory} in ${selectedLocation.label}`,
              getSearchUrl({ taxonomyKey })
            );
          })}
        />
      ),
    });

    // Vendors in nearby cities
    if (seoNearbyCities.length > 0) {
      items.push({
        title: <Title text="Vendors in Nearby Cities" />,
        content: (
          <Content
            items={seoNearbyCities.map((city) =>
              getContentItem(
                `Wedding Vendors in ${city.name}, ${city.stateCode}`,
                getSearchUrl({ location: city })
              )
            )}
          />
        ),
      });
    }
  }

  // Links for NYC + no category SRPs
  if (selectedLocation?.slug === 'new-york-ny' && !selectedTaxonomy && !hasSelectedFacets) {
    // Venues by borough
    items.push({
      title: <Title text="Wedding Venues by Borough" />,
      content: (
        <Content
          items={NYC_BOROUGHS.map((location) =>
            getContentItem(
              `Wedding Venues in ${location.name}`,
              getSearchUrl({ location, taxonomyKey: VENUES_TAXONOMY_KEY })
            )
          )}
        />
      ),
    });

    // Vendors by borough
    items.push({
      title: <Title text="Wedding Vendors by Borough" />,
      content: (
        <Content
          items={NYC_BOROUGHS.map((location) =>
            getContentItem(`Wedding Vendors in ${location.name}`, getSearchUrl({ location }))
          )}
        />
      ),
    });
  }

  // Links for NYC + category SRPs
  if (selectedLocation?.slug === 'new-york-ny' && selectedTaxonomy && !hasSelectedFacets) {
    const category = getVendorWordCategory(selectedTaxonomy.key);

    // Category in borough
    items.push({
      title: <Title text={`Wedding ${category} by Borough`} />,
      content: (
        <Content
          items={NYC_BOROUGHS.map((location) =>
            getContentItem(`Wedding ${category} in ${location.name}`, getSearchUrl({ location }))
          )}
        />
      ),
    });
  }

  // Links for NYC boroughs + no category SRPs
  if (
    selectedLocation?.type === 'CITY' &&
    isBorough(selectedLocation) &&
    !selectedTaxonomy &&
    !hasSelectedFacets
  ) {
    // Wedding vendors in borough
    items.push({
      title: <Title text={`Wedding Vendors in ${selectedLocation.label}`} />,
      content: (
        <Content
          items={SEARCHABLE_VENDOR_TAXONOMY_KEYS.map((taxonomyKey) => {
            const searchableCategory = getVendorWordCategory(taxonomyKey);
            return getContentItem(
              `Wedding ${searchableCategory} in ${selectedLocation.label}`,
              getSearchUrl({ taxonomyKey })
            );
          })}
        />
      ),
    });

    // Nearby boroughs
    items.push({
      title: <Title text="Search Nearby" />,
      content: (
        <Content
          items={NYC_BOROUGHS.map((location) =>
            getContentItem(`Wedding Vendors in ${location.name}`, getSearchUrl({ location }))
          )}
        />
      ),
    });
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <PageSection className={cx(styles.seoCrossLinks, className)}>
      <InlineAccordion items={items} />
    </PageSection>
  );
};
